import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom-v5-compat";
import type { AirBookingFlightItinerarySlice } from "@b2bportal/air-booking-api";
import {
  setSupportProductInfo,
  TravelCategory,
} from "@b2bportal/core-self-serve";
import {
  type AppDispatch,
  type SliceChangeContext,
  unpackExternalResource,
} from "@b2bportal/core-types";
import {
  getScheduleChangeDiffs,
  getSliceChangeContext,
} from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { DateTimeFormatStyle, formatDateTime } from "@hopper-i18n/formatter";
import dayjs from "dayjs";

import { getAirports } from "../../features/postbooking/store/selectors";
import {
  fetchFlightItineraryAndSelect,
  FlightPostBookingSelectors,
} from "../../store";
import type { AirportMap } from "../../types";
import type {
  FlightItineraryDetailPageProps,
  UseFlightItineraryDetailPageReturn,
} from "./types";

export const useFlightItineraryDetailPage = ({
  tripsRoute,
}: FlightItineraryDetailPageProps): UseFlightItineraryDetailPageReturn => {
  const { language } = useI18nContext();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const itineraries = useSelector(
    FlightPostBookingSelectors.getFlightItineraries
  );
  const params = useParams<{ reservationId: string }>();

  const navigateToTrips = React.useCallback(() => {
    navigate(tripsRoute, { replace: true });
  }, [tripsRoute, navigate]);

  // Load the flight itinerary
  React.useEffect(() => {
    if (params.reservationId == null) {
      dispatch(setSupportProductInfo());
      return navigateToTrips();
    }
    dispatch(
      fetchFlightItineraryAndSelect({
        itineraryId: params.reservationId,
        referenceDateTime: dayjs().format(),
      })
    );
    dispatch(
      setSupportProductInfo({
        productId: params.reservationId,
        productType: TravelCategory.Flight,
      })
    );

    return () => {
      dispatch(setSupportProductInfo());
    };
  }, [dispatch, navigateToTrips, params.reservationId]);

  const foundItinerary = React.useMemo(() => {
    if (itineraries.data == null || params.reservationId == null) {
      return null;
    }
    const { data } = itineraries;
    return Object.values(data)
      .flatMap((itins) => itins)
      .find(
        (itinerary) => itinerary.bookedItinerary.id === params.reservationId
      );
  }, [itineraries, params.reservationId]);

  const selectedItinerary = useSelector(
    FlightPostBookingSelectors.getSelectedFlightItinerary
  );

  const fetchedItineraryResource = React.useMemo(
    () => unpackExternalResource(selectedItinerary),
    [selectedItinerary]
  );
  const fetchedItinerary = React.useMemo(
    () =>
      fetchedItineraryResource.data.data ??
      fetchedItineraryResource.data.cached,
    [fetchedItineraryResource]
  );

  React.useEffect(() => {
    // If we've tried to fetch the itinerary and it failed, and we couldn't
    // find the itinerary in the map
    if (foundItinerary == null && fetchedItineraryResource.isError) {
      // TODO: This is handling the errors where we were unable to fetch the
      // itinerary and cannot present the user with cached information from the
      // list either.
      // Since this is a network error, we should present these errors to the
      // user once we have the correct designs for them.
      navigateToTrips();
    }
  }, [foundItinerary, fetchedItineraryResource, navigateToTrips, tripsRoute]);

  const data = React.useMemo(() => {
    const itinerary = fetchedItinerary ?? foundItinerary;

    if (itinerary == null) return null;

    const {
      scheduleChange,
      travelItinerary: { slices },
    } = itinerary.bookedItinerary;
    const scheduleChangeDiffs = getScheduleChangeDiffs(slices, scheduleChange);
    const itineraryData = {
      itinerary,
      scheduleChangeDiffs,
      outboundSlice: slices[0],
      returnSlice: null as AirBookingFlightItinerarySlice | null,
      scheduleChangeContext: {
        outboundSlice: getSliceChangeContext(slices[0], scheduleChangeDiffs),
        returnSlice: undefined as SliceChangeContext | undefined,
      },
    };

    if (slices.length > 1) {
      itineraryData.returnSlice = slices[1];
      itineraryData.scheduleChangeContext.returnSlice = getSliceChangeContext(
        slices[1],
        scheduleChangeDiffs
      );
    }

    return itineraryData;
  }, [foundItinerary, fetchedItinerary]);

  const airports = useSelector(getAirports);

  const context = React.useMemo(() => {
    if (data == null) return null;
    const {
      itinerary,
      outboundSlice,
      returnSlice,
      scheduleChangeDiffs,
      scheduleChangeContext,
    } = data;

    return {
      itinerary,
      scheduleChangeDiffs,
      scheduleChangeContext,
      travelers: [
        ...itinerary.bookedItinerary.passengers.alone.map(
          (traveler) => traveler.person
        ),
        ...itinerary.bookedItinerary.passengers.withLapInfants.flatMap(
          (traveler) => [traveler.adult.person, traveler.infant.person]
        ),
      ],
      outboundSlice: {
        slice: outboundSlice,
        ...collectSliceText(outboundSlice, airports, language),
      },
      returnSlice:
        returnSlice == null
          ? null
          : {
              slice: returnSlice,
              ...collectSliceText(returnSlice, airports, language),
            },
    };
  }, [airports, data, language]);

  return React.useMemo(
    () => ({
      context,
      state: {
        isLoading: fetchedItineraryResource.isLoading,
        isError: fetchedItineraryResource.isError,
      },
    }),
    [context, fetchedItineraryResource]
  );
};

const collectSliceText = (
  slice: AirBookingFlightItinerarySlice,
  airports: AirportMap,
  language: string
) => {
  const lastSegmentIndex = slice.segments.length - 1;
  const firstSegment = slice.segments[0];
  const lastSegment = slice.segments[lastSegmentIndex];
  return {
    destinationCity: cityName(lastSegment.destination.locationCode, airports),
    formattedDepartureDate: formatDateTime(
      dayjs(
        firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture
      ).toDate(),
      language,
      DateTimeFormatStyle.ShortMonthDayShortWeekday
    ),
  };
};

const cityName = (locationCode: string, airportMap: AirportMap) => {
  const airport = airportMap[locationCode];
  return airport?.cityName ?? locationCode;
};
